import { useEffect, useState } from "react";
import { postSuggestChangelog } from "../auth/api-client";
import { useAuth } from "../auth/AuthContext";
import { AI_SPEND_TOOLTIP, AiSpendIcon } from "./ai-spend";

type WhatsChangedModalProps = {
  readonly open: boolean;
  readonly kind: "lessons" | "courses";
  readonly title: string;
  /** Snapshot of the previously published content, for AI diffing. */
  readonly before?: unknown;
  /** Current unsaved draft content, for AI diffing. */
  readonly after: unknown;
  readonly saving?: boolean;
  readonly onCancel: () => void;
  readonly onConfirm: (changelog: string) => void;
};

/**
 * Prompts the author to describe what changed when re-saving a published
 * lesson or course. Offers an AI autofill that diffs the before/after content.
 */
export function WhatsChangedModal({
  open,
  kind,
  title,
  before,
  after,
  saving,
  onCancel,
  onConfirm,
}: WhatsChangedModalProps) {
  const { getBearerToken } = useAuth();
  const bearer = getBearerToken();
  const [note, setNote] = useState("");
  const [suggesting, setSuggesting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setNote("");
      setError(null);
    }
  }, [open]);

  if (!open) return null;

  const noun = kind === "courses" ? "course" : "lesson";

  async function handleSuggest() {
    if (!bearer || suggesting) return;
    setSuggesting(true);
    setError(null);
    try {
      const { changelog } = await postSuggestChangelog(bearer, kind, { before, after });
      if (changelog) setNote(changelog);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not suggest a summary");
    } finally {
      setSuggesting(false);
    }
  }

  return (
    <div className="ccs-modal-overlay" role="dialog" aria-modal="true" aria-label={`What changed in ${title}`}>
      <div className="teacher-modal-card">
        <h2 className="teacher-modal-title">This {noun} is published</h2>
        <p className="muted">
          Your edits to <strong>{title}</strong> create a new version. Tell teachers who follow it what changed so they
          can decide whether to update.
        </p>
        {error ? (
          <div className="warn-banner" role="alert">
            {error}
          </div>
        ) : null}
        <textarea
          className="input"
          rows={5}
          value={note}
          placeholder="e.g. Added a hint to step 3 and fixed the failing test for empty input."
          onChange={(ev) => setNote(ev.target.value)}
          style={{ width: "100%", resize: "vertical" }}
        />
        <div className="teacher-modal-actions">
          <button
            type="button"
            className="btn secondary"
            disabled={suggesting}
            title={AI_SPEND_TOOLTIP}
            onClick={() => void handleSuggest()}
          >
            <AiSpendIcon /> {suggesting ? "Thinking…" : "Let AI summarize"}
          </button>
          <span style={{ flex: 1 }} />
          <button type="button" className="btn ghost" disabled={saving} onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className="btn primary"
            disabled={saving || note.trim().length === 0}
            onClick={() => onConfirm(note.trim())}
          >
            {saving ? "Saving…" : "Save new version"}
          </button>
        </div>
      </div>
    </div>
  );
}
